import React, { useState, useEffect } from 'react';
import styles from './LanguageToggle.module.scss';
import { otherLanguages, suggestedLanguages } from './languages';
const exitSvg = '/icons/close.svg';
import useIsRtl from '../../../app/helpers/isRtl';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Loader from '../loader/Loader';
import Text from '../text/Text';
import measureConnectionSpeed from './measureConnectionSpeed';
import LangModal from './LangModal';
import flagsImage from './flagSprite42.png';
import SimpleBar from 'simplebar-react';
import 'simplebar-react/dist/simplebar.min.css';
import Tooltip from '../tooltip/Tooltip';
import { useMediaQuery } from 'react-responsive';

const GT_CLEANUP_HASH_KEY = 'gt_cleanup_hash';
const GT_RETRY_KEY = 'gt_retry_count';

function getRootDomain(hostname) {
  if (!hostname) return '';
  if (hostname === 'localhost' || !hostname.includes('.')) return hostname;
  const parts = hostname.split('.');
  return parts.slice(-2).join('.');
}

function setCookie(name, value, { domain, path = '/', days = 365 } = {}) {
  const d = new Date();
  d.setTime(d.getTime() + days * 24 * 60 * 60 * 1000);
  const expires = `; expires=${d.toUTCString()}`;
  const p = `; path=${path}`;
  const dom = domain ? `; domain=${domain}` : '';
  document.cookie = `${name}=${value}${expires}${p}${dom}`;
}

function deleteCookie(name, { domain, path = '/' } = {}) {
  const expires = '; expires=Thu, 01 Jan 1970 00:00:00 GMT';
  const p = `; path=${path}`;
  const dom = domain ? `; domain=${domain}` : '';
  document.cookie = `${name}=null${expires}${p}${dom}`;
}

function readGoogTrans() {
  const m = document.cookie.match(/(?:^|;\s*)googtrans=([^;]+)/);
  return m ? decodeURIComponent(m[1]) : null;
}

function hasTranslateMarker() {
  const html = document.documentElement;
  return (
    html.classList.contains('translated-ltr') ||
    html.classList.contains('translated-rtl')
  );
}

function ensureHash(cookieValue) {
  const expected = `#googtrans=${cookieValue}`;
  if (window.location.hash !== expected) {
    window.location.hash = expected;
  }
}

function removeHash() {
  if (typeof history !== 'undefined') {
    history.replaceState(
      null,
      '',
      window.location.pathname + window.location.search
    );
  } else {
    window.location.hash = '';
  }
}

const LanguageToggle = ({ className, titleClassname, isNormalHeader }) => {
  const { i18n } = useTranslation();
  const language = i18n.language;
  const [isBlurry, setIsBlurry] = useState(true);
  const [isAtTop, setIsAtTop] = useState(true);
  const isMobile = useMediaQuery({ maxWidth: 1200 });

  const [isLangLoading, setIsLangLoading] = useState(true);
  const location = useLocation();
  const [selectedLanguage, setSelectedLanguage] = useState(
    localStorage.getItem('language') || 'ar'
  );
  const [isModalOpen, setIsModalOpen] = useState(false);
  const isRtl = useIsRtl();

  const clearGoogleCookies = () => {
    const host = window.location.hostname;
    const root = getRootDomain(host);

    deleteCookie('googtrans', { domain: `.${root}` });
    deleteCookie('googtrans', { domain: host });
    deleteCookie('googtrans', {});

    removeHash();

    localStorage.removeItem(GT_CLEANUP_HASH_KEY);
    localStorage.removeItem(GT_RETRY_KEY);
  };

  const applyGoogleTranslation = (targetLang) => {
    const host = window.location.hostname;
    const root = getRootDomain(host);
    const cookieValue = `/ar/${targetLang}`;

    clearGoogleCookies();

    setCookie('googtrans', cookieValue, { domain: `.${root}` });
    setCookie('googtrans', cookieValue, { domain: host });
    setCookie('googtrans', cookieValue);

    ensureHash(cookieValue);

    localStorage.setItem(GT_CLEANUP_HASH_KEY, '1');

    window.location.reload();
  };

  useEffect(() => {
    const storedLang = localStorage.getItem('language');
    if (storedLang && storedLang !== selectedLanguage) {
      setSelectedLanguage(storedLang);
    }
  }, []);

  useEffect(() => {
    if (localStorage.getItem(GT_CLEANUP_HASH_KEY) === '1') {
      const t = setTimeout(() => {
        removeHash();
        localStorage.removeItem(GT_CLEANUP_HASH_KEY);
      }, 1500);
      return () => clearTimeout(t);
    }
  }, []);

  useEffect(() => {
    if (selectedLanguage === 'ar') {
      const hadTranslate =
        !!readGoogTrans() || window.location.hash.startsWith('#googtrans');
      i18n.changeLanguage('ar');
      if (hadTranslate) {
        clearGoogleCookies();
        window.location.reload();
      } else {
        clearGoogleCookies();
      }
      return;
    }

    if (i18n.language !== 'en') i18n.changeLanguage('en');

    const expected = `/ar/${selectedLanguage}`;
    const current = readGoogTrans();

    if (current !== expected) {
      applyGoogleTranslation(selectedLanguage);
      return;
    }
    const retryCount = parseInt(localStorage.getItem(GT_RETRY_KEY) || '0', 10);
    const check = setTimeout(() => {
      if (!hasTranslateMarker() && retryCount < 1) {
        localStorage.setItem(GT_RETRY_KEY, String(retryCount + 1));
        ensureHash(expected);
        // window.location.reload();
      } else if (hasTranslateMarker()) {
        localStorage.removeItem(GT_RETRY_KEY);
      }
    }, 1200);

    return () => clearTimeout(check);
  }, [selectedLanguage]);

  useEffect(() => {
    let timer;
    const run = async () => {
      setIsLangLoading(true);
      const timeout = await measureConnectionSpeed();
      timer = setTimeout(() => {
        setIsLangLoading(false);
      }, timeout);
    };
    run();
    return () => clearTimeout(timer);
  }, [location.pathname]);

  useEffect(() => {
    if (!isLangLoading) {
      setIsBlurry(true);
      const blurTimer = setTimeout(() => setIsBlurry(false), 1000);
      return () => clearTimeout(blurTimer);
    }
  }, [isLangLoading]);

  const handleSelect = (lang) => {
    if (lang === selectedLanguage) return;
    setSelectedLanguage(lang);
    localStorage.setItem('language', lang);
    setIsModalOpen(false);

    if (lang === 'ar') {
      clearGoogleCookies();
      i18n.changeLanguage('ar');
      window.location.reload();
    } else {
      i18n.changeLanguage('en');
      applyGoogleTranslation(lang);
    }
  };

  const getCurrentLanguage = () =>
    [...suggestedLanguages, ...otherLanguages].find(
      (l) => l.code === selectedLanguage
    ) || {
      code: 'ar',
      label: 'عربي',
      flagCode: 'ae',
    };

  return (
    <>
      {isLangLoading && language !== 'ar' && !isMobile ? (
        <Loader fullPage hideBackground />
      ) : null}

      <div
        onClick={(e) => {
          if (!e.isTrusted) return;
          setIsModalOpen(true);
        }}
        className={`${styles['language-toggle']} ${className || ''}`}
      >
        <Text
          title={getCurrentLanguage()?.label || 'عربي'}
          variant="text-regular-14"
          className={` ${!isNormalHeader ? styles['title-normal'] : styles['title']} ${titleClassname || ''}`}
        />
      </div>

      <LangModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        dialogClassName={styles.modal}
        blurry
        topBottomBehaviour
      >
        <div
          className={`${styles['modal-header']} ${isAtTop ? styles['not-absolute'] : ''}`}
          style={{ direction: isRtl ? 'rtl' : 'ltr' }}
        >
          <div className={styles['choose-lang']}>
            <span
              className={`material-icons ${styles.icon} notranslate`}
              style={{ fontSize: '36px' }}
            >
              language
            </span>
            <Text
              variant="text-bold-italic-25"
              title={i18n.t('choose_language')}
              className={styles['title-lang']}
              color="#1a1a1a"
            />
          </div>

          <img
            src={exitSvg}
            alt="Close"
            onClick={() => setIsModalOpen(false)}
          />
        </div>

        <div
          style={{
            direction: isRtl ? 'rtl' : 'ltr',
            textAlign: isRtl ? 'right' : 'left',
          }}
          className={styles['language-modal']}
        >
          <SimpleBar
            className={`${styles['modal-body']} ${!isAtTop ? styles['no-padding'] : ''} ${isRtl ? styles['rtl-scroll'] : ''}`}
            style={{
              height: '590px',
              marginTop: isAtTop ? '70px' : '0px',
              paddingTop: isAtTop ? '0px' : '90px',
            }}
            onScrollCapture={(e) => {
              const target = e.target;
              setIsAtTop(target.scrollTop === 0);
            }}
          >
            <Text
              variant="text-bold-18"
              title={`اللغات المقترحة  (${suggestedLanguages.length})`}
              className={styles['title-lang']}
              color="#1a1a1a"
              style={{ textTransform: 'capitalize' }}
            />

            <ul className={styles['language-grid']} dir={isRtl ? 'rtl' : ''}>
              {[...suggestedLanguages].map(
                ({ code, label, flagCode, tooltip, isSpecialLang }) => (
                  <li
                    key={code}
                    onClick={() => handleSelect(code)}
                    className={`${styles['language-item']} ${selectedLanguage === code ? styles.active : ''}`}
                    style={{
                      direction: isRtl ? 'rtl' : 'ltr',
                      flexDirection: isRtl ? 'row-reverse' : 'row-reverse',
                    }}
                  >
                    {selectedLanguage === code ? (
                      <div
                        className={`${isRtl ? styles['rtl-circle'] : styles.circle}`}
                      >
                        <span
                          className={'material-icons notranslate'}
                          style={{ color: '#008c4a', fontSize: '16px' }}
                        >
                          check_circle
                        </span>
                      </div>
                    ) : null}
                    <div className={styles['item']}>
                      <Text
                        variant="text-regular-16"
                        title={label}
                        className={`${styles.title} notranslate ${isSpecialLang ? styles['adjust-font'] : ''}`}
                        color="#1a1a1a"
                      />
                      <Tooltip content={tooltip}>
                        <div className={styles['custom-circle']}>
                          <div
                            className={`${styles.flagIcon} ${isSpecialLang ? styles['adjust-font'] : ''} ${
                              flagCode === 'by' ? styles['belarus-flag'] : ''
                            } ${flagCode === 'np' ? styles['nepal-flag'] : ''} ${
                              styles[`fflag-${flagCode?.toUpperCase() || ''}`]
                            }`}
                            style={{ backgroundImage: `url(${flagsImage})` }}
                          />
                        </div>
                      </Tooltip>
                    </div>
                  </li>
                )
              )}
            </ul>

            <div className={styles['other-languages']}>
              <Text
                variant="text-bold-18"
                title={`لغات أخرى (${otherLanguages.length + 1})`}
                className={styles['title-lang']}
                color="#1a1a1a"
              />
              <Text
                variant="text-regular-14"
                title="اللغات التالية مقدمة عبر ترجمة Google"
                className={styles['title-lang-12']}
                color="#1a1a1a"
              />
            </div>

            <ul className={styles['language-grid']}>
              {otherLanguages.map(
                ({ code, label, flagCode, tooltip, isSpecialLang }) => (
                  <li
                    key={code}
                    onClick={() => handleSelect(code)}
                    className={`${styles['language-item']} ${selectedLanguage === code ? styles.active : ''}`}
                    style={{
                      direction: isRtl ? 'rtl' : 'ltr',
                      flexDirection: isRtl ? 'row-reverse' : 'row-reverse',
                    }}
                  >
                    {selectedLanguage === code ? (
                      <div
                        className={`${isRtl ? styles['rtl-circle'] : styles.circle}`}
                      >
                        <span
                          className={'material-icons notranslate'}
                          style={{ color: '#008c4a', fontSize: '16px' }}
                        >
                          check_circle
                        </span>
                      </div>
                    ) : null}
                    <Text
                      variant="text-regular-16"
                      title={label}
                      className={`${styles.title} notranslate`}
                      color="#1a1a1a"
                    />
                    <Tooltip content={tooltip}>
                      <div className={styles['custom-circle']}>
                        <div
                          className={`${styles.flagIcon} ${isSpecialLang ? styles['adjust-font'] : ''} ${
                            flagCode === 'by' ? styles['belarus-flag'] : ''
                          } ${flagCode === 'np' ? styles['nepal-flag'] : ''} ${
                            styles[`fflag-${flagCode?.toUpperCase() || ''}`]
                          }`}
                          style={{ backgroundImage: `url(${flagsImage})` }}
                        />
                      </div>
                    </Tooltip>
                  </li>
                )
              )}
            </ul>
          </SimpleBar>
        </div>
      </LangModal>
    </>
  );
};

export default LanguageToggle;
